import { supportedPaymentMethods, SupportedPaymentMethodsTag } from "consts/ecommerce";
import { WebRoutes } from "consts/enpoints/web";
import type { OnboardingPaymentMethod } from "features/ecommerce/types";
import { putOnboardingsPaymentMethodsSteps } from "features/ecommerce/api";
import { Merchant } from "ui/forms/ECommerce";
import { useOnboardingForm, useOnboardingStepData } from "ui/organizms/ECommerce/Onboarding/common";
import useCountriesAvailable from "./useCountriesAvailable";

type UsePaymentMethodsArg = {
    readonly getApplicationModel: () => Map<number, {
        readonly index: number;
        readonly isIncomplete: boolean;
        readonly hasRequestedChange: boolean;
        readonly countable?: boolean;
        readonly layout?: {
            readonly stepContainerSize: string | number;
        };
    }>;
};

export const usePaymentMethods = ({ getApplicationModel }: UsePaymentMethodsArg) => {
    const { data } = useOnboardingStepData<OnboardingPaymentMethod>();

    const { getCountriesAvailable } = useCountriesAvailable([
        SupportedPaymentMethodsTag.Bancontact,
        SupportedPaymentMethodsTag.Blik,
        SupportedPaymentMethodsTag.Eps,
        SupportedPaymentMethodsTag.Giropay,
        SupportedPaymentMethodsTag.Ideal,
        SupportedPaymentMethodsTag.Mbway,
        SupportedPaymentMethodsTag.Klarna,
        SupportedPaymentMethodsTag.P24
    ]);

    const withValues = useOnboardingForm({
        save: putOnboardingsPaymentMethodsSteps,
        route: WebRoutes.Onboarding.Merchant,
        getApplicationModel
    });

    const paymentMethods = new Map<SupportedPaymentMethodsTag, Merchant.PaymentMethods.PaymentMethod>()
        .set(SupportedPaymentMethodsTag.Bancontact, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Bancontact],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Bancontact)
        })
        .set(SupportedPaymentMethodsTag.Blik, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Blik],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Blik)
        })
        .set(SupportedPaymentMethodsTag.Eps, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Eps],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Eps)
        })
        .set(SupportedPaymentMethodsTag.Giropay, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Giropay],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Giropay)
        })
        .set(SupportedPaymentMethodsTag.Ideal, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Ideal],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Ideal)
        })
        .set(SupportedPaymentMethodsTag.Mbway, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Mbway],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Mbway)
        })
        .set(SupportedPaymentMethodsTag.Klarna, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Klarna],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Klarna)
        })
        .set(SupportedPaymentMethodsTag.P24, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.P24],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.P24)
        });

    const getPaymentMethods = () => Array.from(paymentMethods.values());

    const formProps: Merchant.PaymentMethods.OnboardingPaymentMethodsForm = {
        isBootstrapDataLoading: false,
        bootstrapData: {
            methods: getPaymentMethods()
        },
        initialValues: Merchant.PaymentMethods.getInitialValues({
            methods: data.methods
                ?.split(',')
                .filter(tag => paymentMethods.has(tag as SupportedPaymentMethodsTag))
        }),
        onSaveOrCreate: async ({
            methods = []
        }, helpers) => (
            withValues({
                methods: Array.from(new Set(methods))
                    .join(',')
            }, helpers)
        )
    };

    return {
        ...formProps,
        ...Merchant.PaymentMethods.useForm(formProps),
        ...data
    };
};

export default usePaymentMethods;
